import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { formatCurrency } from "@offstreetparking/design-system/formatters";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { mergeProcessingFees } from "@offstreetparking/domain/payments";
import { getCountryCurrency } from "@offstreetparking/geography";

type TaxAndFeeItem = {
	description: string;
	total: number;
	taxAndFees?: {
		description: string;
		total: number;
	}[];
};

type PriceBreakdownProps = {
	invoice?: {
		subtotal: number;
		taxAndFees: TaxAndFeeItem[];
		discountItems?: {
			description: string;
			total: number;
		}[];
		discountAmount?: number;
		total: number;
		fixedRateItems?: {
			style: string;
			amount: number;
			units: number;
			total: number;
		}[];
	};
	country?: string;
	hasTotalBorders?: boolean;
};

export const PriceBreakdown = ({ invoice, country, hasTotalBorders = false }: PriceBreakdownProps) => {
	const { t } = useLingui();
	const currency = getCountryCurrency(country ?? undefined);
	const { locale } = useLocale();
	const taxAndFees = invoice ? mergeProcessingFees(invoice.taxAndFees) : [];
	const dayNightItems = (invoice?.fixedRateItems ?? []).filter(
		(item) => item.style === "Day" || item.style === "Night",
	);
	const hasBreakdownRows = taxAndFees.length > 0 || (invoice?.discountItems ?? []).length > 0;
	const formatValue = (valueInCents: number) =>
		formatCurrency({
			valueInCents,
			currency,
			locale,
		});

	return (
		<div className="space-y-2 pt-2">
			<div className="rounded-md bg-neutral-faded px-2 py-1.5">
				<div className="flex justify-between">
					<Text size="sm" weight="medium">
						{t`Pricing details`}
					</Text>
					<Text size="sm" weight="bold">
						{formatValue(invoice?.subtotal ?? 0)}
					</Text>
				</div>
				{dayNightItems.map((item, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static list, no reordering
					<Text key={index} size="sm" intent="muted">
						{item.style === "Night"
							? plural(item.units, {
									one: `# night x ${formatValue(item.amount)}`,
									other: `# nights x ${formatValue(item.amount)}`,
								})
							: plural(item.units, {
									one: `# day x ${formatValue(item.amount)}`,
									other: `# days x ${formatValue(item.amount)}`,
								})}
					</Text>
				))}
			</div>

			{invoice?.discountItems?.map((discount) => (
				<div
					key={discount.description}
					className="flex justify-between rounded-md bg-neutral-faded px-2 py-1.5"
				>
					<Text size="sm">{discount.description}</Text>
					<Text size="sm" weight="bold" className="text-success">
						{formatValue(-discount.total)}
					</Text>
				</div>
			))}

			<div
				className={classNames(
					"ml-auto w-1/2",
					hasTotalBorders && hasBreakdownRows ? "border-neutral border-t" : undefined,
				)}
			>
				{(invoice?.discountItems ?? []).length > 0 && (
					<div className="flex justify-between py-1 pr-2">
						<Text size="sm" intent="muted">
							{t`Subtotal`}
						</Text>
						<Text size="sm" intent="muted">
							{formatValue(Math.max(0, (invoice?.subtotal ?? 0) - (invoice?.discountAmount ?? 0)))}
						</Text>
					</div>
				)}

				{taxAndFees.map((tax) => (
					<div key={tax.description}>
						<div className="flex justify-between py-1 pr-2">
							<Text size="sm" intent="muted">
								{tax.description}
							</Text>
							<Text size="sm" intent="muted">
								{formatValue(tax.total)}
							</Text>
						</div>

						{tax.taxAndFees?.map((appliedTax) => (
							<div key={appliedTax.description} className="flex justify-between py-1 pr-2 pl-4">
								<Text size="sm" intent="muted">
									{appliedTax.description}
								</Text>
								<Text size="sm" intent="muted">
									{formatValue(appliedTax.total)}
								</Text>
							</div>
						))}
					</div>
				))}
			</div>
			<div className={classNames("ml-auto w-1/2 py-2", hasTotalBorders ? "border-neutral border-y" : undefined)}>
				<div className="flex justify-between pr-2 font-bold">
					<Text size="sm" weight="bold">
						{t`Total`}
					</Text>
					<Text size="sm" weight="bold">
						{formatValue(invoice?.total ?? 0)}
					</Text>
				</div>
			</div>
		</div>
	);
};
